import React, { useState, useEffect } from 'react';
import { RecreationalVehicle } from '../../types';
import {
  createRecreationalVehicle,
  updateRecreationalVehicle,
  RecreationalVehicleCreate,
  RecreationalVehicleUpdate,
} from '../../services/memberService';
import Modal from '../shared/Modal';

const RV_TYPES = [
  'Motorhome',
  'Travel Trailer',
  'Fifth Wheel',
  'Pop-Up',
  'Truck Camper',
  'Towable Trailers',
  'Specialty RV',
  'Others',
];

interface RecreationalVehicleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  vehicle?: RecreationalVehicle | null;
  memberId: number;
}

const RecreationalVehicleFormModal: React.FC<RecreationalVehicleFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  vehicle,
  memberId,
}) => {
  const isEdit = !!vehicle;

  const [type, setType] = useState('Motorhome');
  const [description, setDescription] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [length, setLength] = useState<number | ''>('');
  const [amp, setAmp] = useState<number | ''>('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (vehicle) {
        setType(vehicle.type || 'Motorhome');
        setDescription(vehicle.description || '');
        setLicensePlate(vehicle.licensePlate || '');
        setLength(vehicle.length || '');
        setAmp(vehicle.amp || '');
        setActive(vehicle.active);
      } else {
        setType('Motorhome');
        setDescription('');
        setLicensePlate('');
        setLength('');
        setAmp('');
        setActive(true);
      }
      setError(null);
    }
  }, [isOpen, vehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!type.trim()) {
      setError('Vehicle type is required.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && vehicle) {
        const data: RecreationalVehicleUpdate = {
          type: type.trim(),
          description: description.trim() || undefined,
          license_plate: licensePlate.trim() || undefined,
          length: length !== '' ? Number(length) : undefined,
          amp: amp !== '' ? Number(amp) : undefined,
          active,
        };
        await updateRecreationalVehicle(vehicle.id, data);
      } else {
        const data: RecreationalVehicleCreate = {
          type: type.trim(),
          description: description.trim() || undefined,
          license_plate: licensePlate.trim() || undefined,
          length: length !== '' ? Number(length) : undefined,
          amp: amp !== '' ? Number(amp) : undefined,
        };
        await createRecreationalVehicle(memberId, data);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error saving recreational vehicle:', err);
      setError(err.message || 'Failed to save recreational vehicle');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
        {saving ? 'Saving...' : isEdit ? 'Update Vehicle' : 'Add Vehicle'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Recreational Vehicle' : 'Add Recreational Vehicle'}
      size="medium"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="form-modal-body">
        {error && <div className="form-error-banner">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label>Type *</label>
            <select value={type} onChange={e => setType(e.target.value)} required>
              {RV_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>License Plate</label>
            <input type="text" value={licensePlate} onChange={e => setLicensePlate(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Description</label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Length (ft)</label>
            <input
              type="number"
              value={length}
              onChange={e => setLength(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Amp Service</label>
            <input
              type="number"
              value={amp}
              onChange={e => setAmp(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
            />
          </div>
        </div>

        {isEdit && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={active}
                onChange={e => setActive(e.target.checked)}
                style={{ width: 'auto' }}
              />
              Active
            </label>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default RecreationalVehicleFormModal;
